import {
  createActuatorControlPayload,
  createPositionActuatorControlPayload,
} from "../../../physics/actuatorMapping";
import { stopActiveServoSession } from "../../../ik/ikTarget/servoSession";
import { resolveJointUuidFromRef } from "../../../utils/jointUtils";
import { focusNewEntityInInspector } from "../../../utils/focusNewEntityInInspector";
import { useSelectionStore } from "../../../stores/selectionStore";
import { clamp } from "./constants";

const rowClass = (isMobileLayout) =>
  isMobileLayout ? "min-w-0 space-y-0.5 bg-transparent py-0.5 px-0.5" : "space-y-1";

const sliderClass = (isMobileLayout) =>
  isMobileLayout
    ? "w-full h-2 slider outline-none focus:outline-none focus-visible:outline-none"
    : "w-full h-2 slider";

const labelClass = (isMobileLayout) =>
  `min-w-0 truncate text-gray-200 select-none${
    isMobileLayout ? " text-[10px] leading-tight" : " text-xs"
  }`;

const valueClass = (isMobileLayout) =>
  `shrink-0 text-right tabular-nums text-gray-300 select-none${
    isMobileLayout ? " text-[10px]" : " text-sm"
  }`;

const detailClass = (isMobileLayout) =>
  `truncate panel-text-helper select-none -mt-0.5${isMobileLayout ? " text-[9px]" : " text-[11px]"}`;

export const ActuatorMotorControlRow = ({
  actuator,
  actuatorList,
  controlName,
  isMobileLayout,
  isRunning,
  label,
  motorGain,
  motorValues,
  setActuatorMotorControl,
  worker,
}) => {
  const stateKey = controlName;
  if (!stateKey) return null;

  const ctrlMin =
    Array.isArray(actuator?.ctrlrange) && actuator.ctrlrange.length === 2
      ? actuator.ctrlrange[0]
      : -1;
  const ctrlMax =
    Array.isArray(actuator?.ctrlrange) && actuator.ctrlrange.length === 2
      ? actuator.ctrlrange[1]
      : 1;
  const hasValidRange = Number.isFinite(ctrlMin) && Number.isFinite(ctrlMax) && ctrlMax > ctrlMin;
  const sliderMin = hasValidRange ? ctrlMin : -1;
  const sliderMax = hasValidRange ? ctrlMax : 1;

  return (
    <div key={actuator?.uuid || stateKey} className={rowClass(isMobileLayout)}>
      <div className="flex min-w-0 items-center justify-between gap-1.5">
        <span className={labelClass(isMobileLayout)}>{label}</span>
        <span className={valueClass(isMobileLayout)}>
          {(motorValues[stateKey] ?? 0).toFixed(3)}
        </span>
      </div>
      {!isMobileLayout && Array.isArray(actuator.ctrlrange) && actuator.ctrlrange.length === 2 ? (
        <div className="panel-text-helper text-[11px] select-none -mt-0.5">
          {actuator.ctrlrange[0]} .. {actuator.ctrlrange[1]}
        </div>
      ) : null}
      <input
        type="range"
        min={sliderMin}
        max={sliderMax}
        step={0.001}
        value={motorValues[stateKey] ?? 0}
        className={sliderClass(isMobileLayout)}
        onChange={(e) => {
          const raw = parseFloat(e.target.value);
          stopActiveServoSession();
          setActuatorMotorControl(stateKey, raw);
          if (!isRunning || !worker) return;

          const scaledRaw = raw * (Number(motorGain) || 1);
          const scaled = hasValidRange ? clamp(scaledRaw, ctrlMin, ctrlMax) : scaledRaw;
          const payload = createActuatorControlPayload({ [stateKey]: scaled }, actuatorList);
          worker.postMessage({ type: "controls", payload });
        }}
      />
    </div>
  );
};

export const ActuatorPositionControlRow = ({
  actuator,
  actuatorList,
  controlName,
  isMobileLayout,
  isRunning,
  joints,
  label,
  posValues,
  sceneActions,
  setActuatorPositionTarget,
  worker,
}) => {
  const stateKey = controlName;
  if (!stateKey) return null;

  const isTendon = Boolean(actuator?.tendon);
  const reactKey = actuator?.uuid || stateKey;

  if (isTendon) {
    const lo = Array.isArray(actuator.ctrlrange) ? actuator.ctrlrange[0] : 0;
    const hi = Array.isArray(actuator.ctrlrange) ? actuator.ctrlrange[1] : 255;
    const min = Number.isFinite(lo) ? lo : 0;
    const max = Number.isFinite(hi) ? hi : 255;
    const step = (max - min) / 200 || 1;
    const currentValue = posValues[stateKey] ?? min;

    return (
      <div key={reactKey} className={rowClass(isMobileLayout)}>
        <div className="flex min-w-0 items-center justify-between gap-1.5">
          <span className={labelClass(isMobileLayout)}>{label}</span>
          <span className={valueClass(isMobileLayout)}>{currentValue.toFixed(1)}</span>
        </div>
        {actuator.tendon ? (
          <div className={detailClass(isMobileLayout)}>{actuator.tendon}</div>
        ) : null}
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={currentValue}
          className={sliderClass(isMobileLayout)}
          onChange={(e) => {
            const raw = parseFloat(e.target.value);
            stopActiveServoSession();
            setActuatorPositionTarget(stateKey, raw);
            if (!isRunning || !worker) return;

            const loClamp = Number.isFinite(lo) ? lo : 0;
            const hiClamp = Number.isFinite(hi) ? hi : 255;
            const clamped =
              Number.isFinite(loClamp) && Number.isFinite(hiClamp) && hiClamp > loClamp
                ? clamp(raw, loClamp, hiClamp)
                : raw;
            const payload = createPositionActuatorControlPayload(
              { [stateKey]: clamped },
              actuatorList
            );
            worker.postMessage({ type: "controls", payload });
          }}
        />
      </div>
    );
  }

  const resolvedUuid = actuator?.joint ? resolveJointUuidFromRef(actuator.joint, joints) : null;
  const jointEntry = resolvedUuid ? joints[resolvedUuid] : null;
  const ctrlLo =
    Array.isArray(actuator?.ctrlrange) && actuator.ctrlrange.length === 2
      ? actuator.ctrlrange[0]
      : null;
  const ctrlHi =
    Array.isArray(actuator?.ctrlrange) && actuator.ctrlrange.length === 2
      ? actuator.ctrlrange[1]
      : null;
  const hasCtrlRange = Number.isFinite(ctrlLo) && Number.isFinite(ctrlHi) && ctrlHi > ctrlLo;
  const jointLo = jointEntry?.range?.[0];
  const jointHi = jointEntry?.range?.[1];
  const hasJointRange = Number.isFinite(jointLo) && Number.isFinite(jointHi) && jointHi > jointLo;
  const min =
    hasCtrlRange && hasJointRange
      ? Math.max(ctrlLo, jointLo)
      : hasCtrlRange
        ? ctrlLo
        : hasJointRange
          ? jointLo
          : -Math.PI;
  const max =
    hasCtrlRange && hasJointRange
      ? Math.min(ctrlHi, jointHi)
      : hasCtrlRange
        ? ctrlHi
        : hasJointRange
          ? jointHi
          : Math.PI;
  const step = (max - min) / 200 || 0.01;
  const currentValue = posValues[stateKey] ?? 0;

  return (
    <div key={reactKey} className={rowClass(isMobileLayout)}>
      <div className="flex min-w-0 items-center justify-between gap-1.5">
        <span className={labelClass(isMobileLayout)}>{label}</span>
        <span className={valueClass(isMobileLayout)}>{currentValue.toFixed(3)}</span>
      </div>
      {jointEntry?.name ? (
        <div className={detailClass(isMobileLayout)}>{jointEntry.name}</div>
      ) : null}
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={currentValue}
        className={sliderClass(isMobileLayout)}
        onChange={(e) => {
          const raw = parseFloat(e.target.value);
          const clampedUi =
            Number.isFinite(min) && Number.isFinite(max) && max > min ? clamp(raw, min, max) : raw;
          stopActiveServoSession();
          setActuatorPositionTarget(stateKey, clampedUi);
          if (!isRunning && actuator?.joint && sceneActions?.updateJointValue) {
            const jointUuid = resolveJointUuidFromRef(actuator.joint, joints);
            if (jointUuid) sceneActions.updateJointValue(jointUuid, clampedUi);
          }
          if (!isRunning || !worker) return;

          const payload = createPositionActuatorControlPayload(
            { [stateKey]: clampedUi },
            actuatorList
          );
          worker.postMessage({ type: "controls", payload });
        }}
      />
    </div>
  );
};

export const JointPositionControlRow = ({
  isMobileLayout,
  isRunning,
  joint,
  jointUuid,
  label,
  sceneActions,
  selectedJoint,
}) => {
  const reactKey = jointUuid || joint?.uuid || label;
  const range =
    Array.isArray(joint?.range) && joint.range.length === 2 ? joint.range : [-Math.PI, Math.PI];
  const min = Number.isFinite(Number(range[0])) ? Number(range[0]) : -Math.PI;
  const max = Number.isFinite(Number(range[1])) ? Number(range[1]) : Math.PI;
  const hasValidRange = max > min;
  const currentValue = Number.isFinite(Number(joint?.ref)) ? Number(joint.ref) : 0;
  const sliderValue = hasValidRange ? clamp(currentValue, min, max) : currentValue;
  const step = hasValidRange ? (max - min) / 200 || 0.01 : 0.01;
  const isSelectedJoint = Boolean(jointUuid && selectedJoint === jointUuid);

  const openJointProperties = () => {
    if (!jointUuid) return;
    focusNewEntityInInspector("actuatorControls", () =>
      useSelectionStore.getState().selectJoint(jointUuid)
    );
  };
  const stopSliderClickThrough = (event) => {
    event.stopPropagation();
  };

  return (
    <div
      key={reactKey}
      role="button"
      tabIndex={0}
      aria-current={isSelectedJoint ? "true" : undefined}
      className={
        isMobileLayout
          ? `min-w-0 space-y-0.5 rounded-md border px-0.5 py-0.5 transition-colors hover:bg-transparent focus:outline-none focus-visible:outline-none ${
              isSelectedJoint
                ? "border-white bg-transparent shadow-[0_0_0_1px_rgba(255,255,255,0.9)]"
                : "border-transparent bg-transparent"
            }`
          : `space-y-1 rounded-md border px-2 py-1.5 transition-colors hover:bg-gray-800/60 focus:outline-none focus-visible:ring-1 focus-visible:ring-gray-500 ${
              isSelectedJoint
                ? "border-white bg-transparent shadow-[0_0_0_1px_rgba(255,255,255,0.9)]"
                : "border-gray-700 bg-transparent"
            }`
      }
      onClick={openJointProperties}
      onKeyDown={(event) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        event.preventDefault();
        openJointProperties();
      }}
    >
      <div className="flex min-w-0 items-center justify-between gap-1.5">
        <span className={labelClass(isMobileLayout)}>{label}</span>
        <span className={valueClass(isMobileLayout)}>{sliderValue.toFixed(3)}</span>
      </div>
      <input
        type="range"
        min={hasValidRange ? min : -Math.PI}
        max={hasValidRange ? max : Math.PI}
        step={step}
        value={sliderValue}
        className={sliderClass(isMobileLayout)}
        disabled={isRunning}
        onClick={stopSliderClickThrough}
        onPointerDown={stopSliderClickThrough}
        onKeyDown={stopSliderClickThrough}
        onChange={(e) => {
          if (isRunning || !jointUuid) return;
          const raw = parseFloat(e.target.value);
          const clampedValue = hasValidRange ? clamp(raw, min, max) : raw;
          sceneActions.updateJointValue(jointUuid, clampedValue);
        }}
      />
    </div>
  );
};
